// Library Methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import Alert from "src/components/shared/alerts/Alert/Alert";
import AddEditProgressiveModal from "./AddEditProgressiveModal/AddEditProgressiveModal";
import ProgressiveDescription from "./ProgressiveDescription/ProgressiveDescription";
import DeleteProgressiveModal from "./DeleteProgressiveModal/DeleteProgressiveModal";
import { PlusIcon } from "src/assets/icons";

// Types
import {
  AddEditProgressiveLensType,
  ProgressiveLensType,
} from "src/services/clinics/types";

// Styles
import {
  ModalCustomConfirmDarkCta,
  getSurfaceTokens,
} from "src/styles/mui/muiStylesHelper";
import { UI_SIZES } from "src/constants/uiTokens";

// Hooks
import { useProgressiveLenses } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useProgressiveLenses";
import { useAddProgressiveLens } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useAddProgressiveLens";
import { useUpdateProgressiveLens } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useUpdateProgressiveLens";
import { useDeleteProgressiveLens } from "src/services/clinics/hooks/clinic-lenses/progressive-lenses/useDeleteProgressiveLens";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

type ProgressiveSettingsType = {
  isLoadingSettings: boolean;
  isUpdatingSettings: boolean;
};

const ProgressiveSettings = ({
  isLoadingSettings,
  isUpdatingSettings,
}: ProgressiveSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const [selectedLens, setSelectedLens] = useState<ProgressiveLensType>();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const {
    data: lensesData,
    isLoading: isLoadingLenses,
    isError: isErrorLenses,
    isFetching: isFetchingLenses,
    isRefetching: isRefetchingLenses,
  } = useProgressiveLenses(clinicInfo?.id);

  const { mutate: addLensMutate, isPending: addLensPending } =
    useAddProgressiveLens(clinicInfo?.id);
  const { mutate: updateLensMutate, isPending: updateLensPending } =
    useUpdateProgressiveLens(clinicInfo?.id);
  const { mutate: deleteLensMutate, isPending: deleteLensPending } =
    useDeleteProgressiveLens(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingSettings || isLoadingLenses,
    [isLoadingSettings, isLoadingLenses]
  );

  const isUpdating = useMemo(
    () =>
      isUpdatingSettings ||
      isFetchingLenses ||
      isRefetchingLenses ||
      addLensPending ||
      updateLensPending ||
      deleteLensPending,
    [
      isUpdatingSettings,
      isFetchingLenses,
      isRefetchingLenses,
      addLensPending,
      updateLensPending,
      deleteLensPending,
    ]
  );

  const hasLenses = useMemo(
    () =>
      !isLoadingLenses &&
      !isErrorLenses &&
      lensesData &&
      lensesData?.length > 0,
    [isLoadingLenses, isErrorLenses, lensesData]
  );

  const hasNoLenses = useMemo(
    () => !isLoadingLenses && !isErrorLenses && !lensesData?.length,
    [isLoadingLenses, isErrorLenses, lensesData]
  );

  const addLens = useCallback(
    (lensData: AddEditProgressiveLensType) => {
      addLensMutate(lensData, { onSuccess: () => setOpenAddModal(false) });
    },
    [addLensMutate]
  );

  const updateLens = useCallback(
    (lensData: AddEditProgressiveLensType) => {
      if (!selectedLens) return;

      updateLensMutate(
        { ...selectedLens, ...lensData },
        {
          onSuccess: () => {
            setSelectedLens(undefined);
            setOpenEditModal(false);
          },
        }
      );
    },
    [updateLensMutate, selectedLens]
  );

  const deleteLens = useCallback(() => {
    if (!selectedLens) return;

    deleteLensMutate(selectedLens?.id, {
      onSuccess: () => {
        setSelectedLens(undefined);
        setOpenDeleteModal(false);
      },
    });
  }, [deleteLensMutate, selectedLens]);

  return (
    <>
      <DefaultCard>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="space-between" gap="16px">
            <Box display="flex" flexDirection="column" gap="6px">
              <Typography fontSize="14px" fontWeight={600}>
                {t("Progressive Lens Options")}
              </Typography>

              <Typography fontSize="14px" color={mutedText}>
                {t(
                  "Manage virtual lenses that can be used in lens simulations."
                )}
              </Typography>
            </Box>
            <Button
              disabled={isLoading || isUpdating}
              sx={ModalCustomConfirmDarkCta}
              startIcon={<PlusIcon />}
              onClick={() => setOpenAddModal(true)}
            >
              {t("Add")}
            </Button>
          </Box>

          {isLoading ? (
            <Box display="flex" flexDirection="column" gap="20px">
              <Skeleton
                variant="rounded"
                width="100%"
                height={UI_SIZES.inputHeight}
                sx={{
                  borderRadius: "4px",
                }}
              />
              <Skeleton
                variant="rounded"
                width="100%"
                height={90}
                sx={{
                  borderRadius: "4px",
                }}
              />
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap="20px">
              {hasNoLenses && (
                <Alert
                  variant="warning"
                  alertContent={t("No lens options found.")}
                />
              )}

              {hasLenses && (
                <Box display="flex" flexWrap="wrap" gap="12px">
                  {lensesData?.map((lens, index) => (
                    <Box
                      key={`${lens.name}-${index}`}
                      sx={{
                        flexGrow: 1,
                        flexBasis: {
                          xs: "100%",
                          md: "40%",
                        },
                        minWidth: 0,
                        "& > *": { height: "100%" },
                      }}
                    >
                      <ProgressiveDescription
                        isUpdating={isUpdating}
                        lensData={lens}
                        openDeleteModal={() => {
                          setSelectedLens(lens);
                          setOpenDeleteModal(true);
                        }}
                        openEditModal={() => {
                          setSelectedLens(lens);
                          setOpenEditModal(true);
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DefaultCard>

      <AddEditProgressiveModal
        open={openAddModal}
        title={t("Add Progressive Option")}
        onClose={() => setOpenAddModal(false)}
        onConfirm={addLens}
        isUpdating={isUpdating}
      />

      <AddEditProgressiveModal
        open={openEditModal}
        title={t("Edit Progressive Option")}
        lensToEdit={selectedLens}
        onClose={() => {
          setSelectedLens(undefined);
          setOpenEditModal(false);
        }}
        onConfirm={updateLens}
        isUpdating={isUpdating}
      />

      <DeleteProgressiveModal
        isLoading={isUpdating}
        selectedLens={selectedLens}
        open={openDeleteModal}
        onClose={() => {
          setSelectedLens(undefined);
          setOpenDeleteModal(false);
        }}
        onConfirm={deleteLens}
      />
    </>
  );
};

export default ProgressiveSettings;
